import type { AnyFieldMeta } from "@tanstack/react-form";
import { shouldDisplayFieldError } from "./validation-policy";

/**
 * The minimal field shape the display gate needs. Structural on purpose:
 * `AnyFieldApi` is a class (nominal), which rejects narrowed structural
 * handles like the webapp's `FieldOf<TValue>` — while still rejecting a bare
 * `field.state.meta` argument (meta has no `.state`).
 */
type FieldErrorSource = { state: { meta: AnyFieldMeta } };

/**
 * The message to render under a field, or `undefined` when nothing should show.
 * Applies the display half of the validation policy — callers NEVER gate.
 *
 * Field-local by design: works with any TanStack field, whether it came from
 * `useAppForm`, `InlineEditForm`, or a plain `useForm`. Handles both
 * Zod/StandardSchema errors (`{ message }`) and inline validator strings.
 */
export function getFieldErrorMessage(
  field: FieldErrorSource,
): string | undefined {
  const meta = field.state.meta;
  if (meta.isValid) return undefined;
  if (
    !shouldDisplayFieldError({
      isBlurred: meta.isBlurred,
      isDirty: meta.isDirty,
      hasSubmitError: meta.errorMap?.onSubmit != null,
    })
  ) {
    return undefined;
  }
  const message = meta.errors
    .map((err) => {
      if (typeof err === "string") return err;
      if (err && typeof err === "object" && "message" in err)
        return (err as { message: string }).message;
      return undefined;
    })
    .filter(Boolean)
    .filter((msg, idx, arr) => arr.indexOf(msg) === idx)
    .join(", ");
  return message || undefined;
}
